import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Bell, ShieldAlert, Search } from 'lucide-react';
import { alertsApi } from '../services/api';
import { Card, SectionHeader, Loader, Badge, Button, EmptyState } from '../components/ui';
import toast from 'react-hot-toast';

const TYPE_FILTERS = ['ALL', 'OVERPROVISIONED', 'CRITICAL_METRIC', 'ANOMALY', 'SECURITY'];
const SEVERITY_FILTERS = ['ALL', 'CRITICAL', 'WARNING', 'INFO'];

const SEVERITY_COLORS = {
  CRITICAL: { bg: 'rgba(255,92,92,0.12)', border: 'rgba(255,92,92,0.3)', text: '#ff5c5c', dot: '#ff5c5c' },
  WARNING:  { bg: 'rgba(255,184,0,0.12)', border: 'rgba(255,184,0,0.3)', text: '#ffb800', dot: '#ffb800' },
  INFO:     { bg: 'rgba(74,158,255,0.12)', border: 'rgba(74,158,255,0.3)', text: '#4a9eff', dot: '#4a9eff' },
};

const MOCK_ALERTS = [
  { id: 1, timestamp: new Date(Date.now() - 300000).toISOString(), type: 'CRITICAL_METRIC', severity: 'CRITICAL', resource: 'prod-api-server-01', message: 'CPU utilization exceeded 95% for 10 minutes', acknowledged: false },
  { id: 2, timestamp: new Date(Date.now() - 900000).toISOString(), type: 'OVERPROVISIONED', severity: 'WARNING', resource: 'staging-db-replica-03', message: 'Database instance running at 8% CPU - consider downsizing', acknowledged: false },
  { id: 3, timestamp: new Date(Date.now() - 1800000).toISOString(), type: 'ANOMALY', severity: 'WARNING', resource: 'prod-cache-cluster', message: 'Unusual spike in memory usage detected - 40% above baseline', acknowledged: false },
  { id: 4, timestamp: new Date(Date.now() - 3600000).toISOString(), type: 'SECURITY', severity: 'CRITICAL', resource: 'iam-role-admin', message: 'Unused admin credentials detected - last used 90 days ago', acknowledged: false },
  { id: 5, timestamp: new Date(Date.now() - 5400000).toISOString(), type: 'OVERPROVISIONED', severity: 'INFO', resource: 'dev-worker-pool', message: 'Dev environment workers idle during weekends - schedule recommended', acknowledged: true },
  { id: 6, timestamp: new Date(Date.now() - 7200000).toISOString(), type: 'CRITICAL_METRIC', severity: 'WARNING', resource: 'prod-load-balancer', message: 'Request latency P99 increased to 2.3s (threshold: 1s)', acknowledged: false },
  { id: 7, timestamp: new Date(Date.now() - 10800000).toISOString(), type: 'ANOMALY', severity: 'CRITICAL', resource: 'prod-payment-svc', message: 'Error rate spike: 4.2% (baseline: 0.1%)', acknowledged: false },
  { id: 8, timestamp: new Date(Date.now() - 14400000).toISOString(), type: 'OVERPROVISIONED', severity: 'WARNING', resource: 'staging-k8s-node-pool', message: '12 nodes allocated, average utilization 15%', acknowledged: true },
  { id: 9, timestamp: new Date(Date.now() - 21600000).toISOString(), type: 'SECURITY', severity: 'INFO', resource: 's3-bucket-logs', message: 'Public access block not enabled on logging bucket', acknowledged: false },
  { id: 10, timestamp: new Date(Date.now() - 28800000).toISOString(), type: 'CRITICAL_METRIC', severity: 'WARNING', resource: 'prod-db-primary', message: 'Connection pool at 82% capacity', acknowledged: false },
];

const tableHeaderStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  color: 'rgba(255,255,255,0.35)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

export default function AlertsPage() {
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [localAlerts, setLocalAlerts] = useState(MOCK_ALERTS);
  const qc = useQueryClient();

  const { data: apiAlerts } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => alertsApi.getAll().then((r) => r.data),
    refetchInterval: 30000,
    retry: false,
    onSuccess: (data) => {
      if (Array.isArray(data) && data.length > 0) {
        setLocalAlerts(data);
      }
    },
  });

  const acknowledgeMut = useMutation({
    mutationFn: (id) => alertsApi.acknowledge(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
      qc.invalidateQueries({ queryKey: ['alert-count'] });
    },
    onError: () => {},
  });

  const handleAcknowledge = (id) => {
    setLocalAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, acknowledged: true } : a))
    );
    acknowledgeMut.mutate(id);
    toast.success('Alert acknowledged');
  };

  const alerts = localAlerts;
  const filtered = alerts.filter((a) => {
    if (typeFilter !== 'ALL' && a.type !== typeFilter) return false;
    if (severityFilter !== 'ALL' && a.severity !== severityFilter) return false;
    return true;
  });

  const criticalCount = alerts.filter((a) => a.severity === 'CRITICAL' && !a.acknowledged).length;
  const warningCount = alerts.filter((a) => a.severity === 'WARNING' && !a.acknowledged).length;
  const infoCount = alerts.filter((a) => a.severity === 'INFO' && !a.acknowledged).length;

  return (
    <div>
      <SectionHeader
        title="Alerts Management"
        subtitle="Monitor and manage infrastructure alerts across all resources"
      />

      {/* Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 16,
        marginBottom: 24,
      }}>
        {[
          { label: 'Critical', count: criticalCount, color: '#ff5c5c', icon: ShieldAlert },
          { label: 'Warning', count: warningCount, color: '#ffb800', icon: AlertTriangle },
          { label: 'Info', count: infoCount, color: '#4a9eff', icon: Bell },
        ].map(({ label, count, color, icon: Icon }) => (
          <Card key={label} style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label} Alerts</div>
                <div style={{ color, fontSize: 32, fontWeight: 700 }}>{count}</div>
              </div>
              <div style={{
                width: 42, height: 42, borderRadius: 10,
                background: `${color}18`, border: `1px solid ${color}30`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={20} color={color} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 22, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600 }}>Type:</span>
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${typeFilter === t ? '#0084ff' : 'rgba(255,255,255,0.1)'}`,
                background: typeFilter === t ? 'rgba(0,132,255,0.15)' : 'transparent',
                color: typeFilter === t ? '#4a9eff' : 'rgba(255,255,255,0.45)',
                transition: 'all 0.15s ease',
                fontFamily: 'inherit',
              }}
            >
              {t.replace('_', ' ')}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: 600 }}>Severity:</span>
          {SEVERITY_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setSeverityFilter(s)}
              style={{
                padding: '5px 14px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1px solid ${severityFilter === s ? '#0084ff' : 'rgba(255,255,255,0.1)'}`,
                background: severityFilter === s ? 'rgba(0,132,255,0.15)' : 'transparent',
                color: severityFilter === s ? '#4a9eff' : 'rgba(255,255,255,0.45)',
                transition: 'all 0.15s ease',
                fontFamily: 'inherit',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts Table */}
      <Card>
        {filtered.length === 0 ? (
          <EmptyState message="No alerts match the selected filters" icon={CheckCircle} />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Time', 'Type', 'Severity', 'Resource', 'Message', 'Actions'].map((h) => (
                    <th key={h} style={tableHeaderStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((alert) => {
                  const sc = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.INFO;
                  return (
                    <tr
                      key={alert.id}
                      style={{
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        background: alert.acknowledged ? 'rgba(255,255,255,0.01)' : 'transparent',
                        opacity: alert.acknowledged ? 0.5 : 1,
                        transition: 'all 0.2s',
                      }}
                    >
                      <td style={{ padding: '12px', color: 'rgba(255,255,255,0.6)', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {new Date(alert.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.6)',
                          letterSpacing: '0.03em',
                        }}>
                          {alert.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                          background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                        }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot }} />
                          {alert.severity}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#fff', fontSize: 13, fontWeight: 500, fontFamily: 'monospace' }}>
                        {alert.resource}
                      </td>
                      <td style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontSize: 13, maxWidth: 350 }}>
                        {alert.message}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {!alert.acknowledged ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleAcknowledge(alert.id)}
                          >
                            Acknowledge
                          </Button>
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircle size={12} /> Acknowledged
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
